import {unitsConverter} from './number';

/**
 * This function makes sure that string is converted to follow key pattern we get from BE
 * Currently function replaces - with _ and converts string to lowercase
 * @param {string} input Input string to format
 * @return {string} String that meets same format as BE keys
 */
export const keyFilter = (input: string): string => {
  return input.toLowerCase().replace(/-/g, '_');
};

/**
 * This is the function to display correct artwork dimensions.
 * It will convert to different units based on user selection without any requests to BE.
 * Displayed value will be rounded to whole numbers.
 * @param {object} dimensions Dimensions object
 * @param {string} displaySizeUnits Either 'cm' or 'in'
 * @return {string} Dimensions string matching format
 */
export const getDimensions = (
  dimensions: DimensionsProps,
  displaySizeUnits: DisplayUnitsType,
): string => {
  if (!displaySizeUnits || !dimensions) return '';

  if (displaySizeUnits === 'in') {
    if (dimensions.units === 'cm') {
      return `${unitsConverter.cmToInches(dimensions.width)} x ${unitsConverter.cmToInches(
        dimensions.height,
      )}"`;
    } else {
      return `${unitsConverter.round(dimensions.width)} x ${unitsConverter.round(
        dimensions.height,
      )}"`;
    }
  } else {
    if (dimensions.units === 'in') {
      return `${unitsConverter.inchesToCm(dimensions.width)} x ${unitsConverter.inchesToCm(
        dimensions.height,
      )}cm`;
    } else {
      return `${unitsConverter.round(dimensions.width)} x ${unitsConverter.round(
        dimensions.height,
      )}cm`;
    }
  }
};

export interface DimensionsProps {
  width: number; // decimal
  height: number;
  depth: number | null;
  units: DisplayUnitsType; // 'cm'
  type: string; // "unframed" - why we have it in this place?
}

export type DisplayUnitsType = 'cm' | 'in';

/**
 * Capitalizes the first letter of a given string.
 * @param {string} str Input string to format
 * @return {string} String that meets same format as BE keys
 */
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * @description encodes the search text by replacing spaces and %20 with '_'.
 * @param {string} input search text to encode
 * @return {string} encoded search text
 */
export const encodeSearchText = (input: string) => {
  /**
   * Trim leading/trailing spaces and %20
   */
  const trimmed = input.replace(/^(?:\s|%20)+|(?:\s|%20)+$/g, '');

  /**
   * Replace all inner spaces and %20 (including repeated) with '_'
   * The group (?:\s|%20)+ matches one or more spaces or %20
   */
  return trimmed.replace(/(?:\s|%20)+/g, '_');
};

/**
 * @description decodes the search text by replacing '_' with spaces
 * to be used for the search text earlier encoded by `encodeSearchText`.
 * @param {string} input search text to decode
 * @return {string} decoded search text
 */
export const decodeSearchText = (input: string) => {
  return input.replaceAll(/\_/g, ' ');
};

/**
 * @description Generates a camelCase key based on the input string.
 * @param {string} input pass string like 'Me at work' or 'me-at-work'
 * @return {string} will result in 'meAtWork'
 */
export const makeCamelCase = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase(),
    )
    .replace(/\s+/g, '');
};

export const makeKebabCase = (input: string): string => {
  return input
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // replace spaces with -
    .replace(/[^\w-]+/g, '') // remove all non-word chars except -
    .replace(/--+/g, '-'); // collapse multiple -
};

/**
 * @description Generates a unique key string. Could be used for keys in maps.
 * @return {string} unique key
 */
export const makeUniqueKey = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

/**
 *
 * @param length Maximum length of character to show
 * @param text Text to truncate
 * @returns truncated text with ... at the end
 */
export const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }

  return text.slice(0, maxLength) + '...';
};
